import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "../lib/trpc";
import { useTranslation } from "../i18n";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useLocation } from "wouter";
import { ArrowLeft, TrendingUp, DollarSign, ShoppingCart, Percent } from "lucide-react";
import { ThemeToggle } from "../components/ThemeToggle";
import { DEFAULT_CURRENCY } from "@shared/const";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

export default function Analytics() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [, navigate] = useLocation();

  const { data: overview } = trpc.analytics.getOverview.useQuery();
  const { data: revenueByMonth } = trpc.analytics.getRevenueByMonth.useQuery();
  const { data: topProjects } = trpc.analytics.getTopProjects.useQuery({ limit: 5 });
  const { data: conversion } = trpc.analytics.getConversionRate.useQuery();
  const { data: settings } = trpc.settings.get.useQuery();
  const currency = settings?.currency || DEFAULT_CURRENCY;
  const revenueLabel = (t.analytics.revenueLabel || '').replace('{currency}', currency);

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>{t.analytics.title}</CardTitle>
            <CardDescription>{t.auth.welcomeBack}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              {t.analytics.pleaseLogin}
            </p>
            <Button asChild className="w-full">
              <a href="/api/auth/login">{t.auth.signIn}</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 dark:from-gray-900 dark:via-purple-900 dark:to-gray-900">
      <div className="container mx-auto p-8">
        {/* Back Button */}
        <Button
          variant="outline"
          onClick={() => navigate("/")}
          className="mb-6 gap-2 hover:bg-purple-50 dark:hover:bg-purple-950/30 hover:text-purple-600 dark:hover:text-purple-400 hover:border-purple-300 dark:hover:border-purple-700 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {t.common.back}
        </Button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent flex items-center gap-3">
            <TrendingUp className="h-10 w-10 text-purple-600" />
            {t.analytics.title}
          </h1>
          <p className="text-muted-foreground mt-2 text-lg">
            {t.analytics.subtitle}
          </p>
        </div>
        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-xl hover:shadow-2xl transition-all duration-300">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">{t.analytics.totalRevenue}</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {overview ? `${overview.totalRevenue.toFixed(2)} ${currency}` : `0.00 ${currency}`}
              </div>
              <p className="text-xs text-muted-foreground">
                {t.analytics.fromOrders.replace('{count}', String(overview?.orderCount || 0))}
              </p>
            </CardContent>
          </Card>

          <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-xl hover:shadow-2xl transition-all duration-300">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">{t.analytics.orders}</CardTitle>
              <ShoppingCart className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{overview?.orderCount}</div>
              <p className="text-xs text-muted-foreground">
                {t.analytics.paidOrders}
              </p>
            </CardContent>
          </Card>

          <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-xl hover:shadow-2xl transition-all duration-300">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">{t.analytics.conversion}</CardTitle>
              <Percent className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {conversion?.conversionRate.toFixed(1)}%
              </div>
              <p className="text-xs text-muted-foreground">
                {t.analytics.codesUsed.replace('{converted}', String(conversion?.convertedCodes || 0)).replace('{total}', String(conversion?.totalCodes || 0))}
              </p>
            </CardContent>
          </Card>

          <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-xl hover:shadow-2xl transition-all duration-300">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">{t.analytics.projects}</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{overview?.projectCount}</div>
              <p className="text-xs text-muted-foreground">
                {t.analytics.imagesCount.replace('{count}', String(overview?.imageCount || 0))}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Revenue Chart */}
        <Card className="mb-8 backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-xl hover:shadow-2xl transition-all duration-300">
          <CardHeader>
            <CardTitle>{t.analytics.revenueByMonth}</CardTitle>
            <CardDescription>{t.analytics.last12Months}</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueByMonth || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  name={revenueLabel}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top Projects */}
        <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-xl hover:shadow-2xl transition-all duration-300">
          <CardHeader>
            <CardTitle>{t.analytics.topProjects}</CardTitle>
            <CardDescription>{t.analytics.top5Projects}</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topProjects || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="projectName" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="revenue" fill="hsl(var(--primary))" name={revenueLabel} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
